import { toE164, resolvePageLocale, defaultCountryForLocale } from "@/lib/phone";

const CRM_WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/H0p1tI6pHgm0kD9kja2M/webhook-trigger/549305fd-5f9c-4f66-b736-b8b2e2b666e8";

export interface LeadPayload {
  form_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  preferred_language: string;
  prefers_whatsapp: string;
  case_category: string;
  case_subcategory: string;
  situation_description: string;
  consent_to_contact: string;
}

interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
}

const ATTRIBUTION_KEY = "arandia_attribution";

function getSearchParam(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = new URL(window.location.href).searchParams.get(name);
  return value ?? undefined;
}

export function captureAttribution(): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;

  try {
    const existing = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (existing) return;
  } catch {
    return;
  }

  const params: AttributionData = {
    utm_source: getSearchParam("utm_source"),
    utm_medium: getSearchParam("utm_medium"),
    utm_campaign: getSearchParam("utm_campaign"),
    utm_term: getSearchParam("utm_term"),
    utm_content: getSearchParam("utm_content"),
    gclid: getSearchParam("gclid"),
    fbclid: getSearchParam("fbclid"),
    referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
  };

  const hasAnyParam = Object.values(params).some((value) => value !== undefined && value !== "");
  if (!hasAnyParam) return;

  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(params));
  } catch {
    // Ignore private-mode / storage-disabled errors.
  }
}

function readAttribution(): AttributionData {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return {};

  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as AttributionData;
    }
  } catch {
    // Ignore parse/storage errors.
  }

  return {};
}

function toStringValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value);
}

export async function submitLead(payload: LeadPayload): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const attribution = readAttribution();
  const locale = resolvePageLocale();

  const body: Record<string, string> = {
    form_name: toStringValue(payload.form_name),
    first_name: toStringValue(payload.first_name),
    last_name: toStringValue(payload.last_name),
    email: toStringValue(payload.email),
    // O PhoneField já entrega E.164. Isto é a rede de segurança para
    // qualquer chamada que ainda passe texto cru: número que não disca
    // vira "", nunca um "+" solto ou um país inventado.
    phone: toE164(toStringValue(payload.phone), defaultCountryForLocale(locale)),
    preferred_language: toStringValue(payload.preferred_language),
    prefers_whatsapp: toStringValue(payload.prefers_whatsapp),
    case_category: toStringValue(payload.case_category),
    case_subcategory: toStringValue(payload.case_subcategory),
    situation_description: toStringValue(payload.situation_description),
    consent_to_contact: toStringValue(payload.consent_to_contact),
    utm_source: toStringValue(attribution.utm_source),
    utm_medium: toStringValue(attribution.utm_medium),
    utm_campaign: toStringValue(attribution.utm_campaign),
    utm_term: toStringValue(attribution.utm_term),
    utm_content: toStringValue(attribution.utm_content),
    gclid: toStringValue(attribution.gclid),
    fbclid: toStringValue(attribution.fbclid),
    referrer: toStringValue(attribution.referrer),
    page_url: window.location.href,
    page_locale: locale,
    consent_timestamp: new Date().toISOString(),
  };

  try {
    const response = await fetch(CRM_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      try {
        sessionStorage.setItem(
          "arandia_lead",
          JSON.stringify({
            first_name: body.first_name,
            preferred_language: body.preferred_language,
          })
        );
      } catch {
        // Ignore storage errors.
      }
      return true;
    }

    return false;
  } catch (err) {
    console.error("[crm] Failed to submit lead:", err);
    return false;
  }
}

export function isLikelyBot(honeypotValue: string, formStartedAt: number): boolean {
  const honeypotFilled = honeypotValue.trim().length > 0;
  const tooFast = Date.now() - formStartedAt < 3000;
  return honeypotFilled || tooFast;
}
